import { Component, OnInit } from '@angular/core';
import {
    ActivatedRoute,
    Router
} from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthService } from '@/auth/services';
import { DataTableComponent } from '@/components';
import {
    DataTableColumnType,
    FilterType
} from '@/enums';
import {
    applyDistrictAndCompetitorToFilterObject,
    applyDistrictAndCompetitorToQuery,
} from '@/helpers';
import { CompetitorMapping, DataTableColumn } from '@/interfaces';
import { CompetitorMappingsService } from '@/service';

@Component({
    selector: 'app-competitor-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    styleUrls: ['./listing.component.scss'],
    imports: [DataTableComponent]
})
export class CompetitorListingComponent implements OnInit {
    reloadTable$ = new Subject<void>();
    loading = false;
    columns: DataTableColumn<CompetitorMapping>[] = [
        {
            field: 'is_mapped',
            header: 'Mapped',
            filterType: FilterType.BOOLEAN,
            filterInToolbar: true,
            tableVisible: false,
            sortable: false,
        },
        {
            field: 'agency_name',
            header: 'Competitor (Raw)',
            type: DataTableColumnType.LINK,
            link: (row: CompetitorMapping) => `/competitor-mappings/${row.id}`,
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.competitorAutocomplete('agency_name', q, context),
        },
        {
            field: 'rates_count',
            header: '# of Occurrences (Raw)',
            filterType: FilterType.NUMERIC
        },
        {
            field: 'is_excluded',
            header: 'Exclude',
            type: DataTableColumnType.CHECKBOX,
            filterType: FilterType.BOOLEAN,
            filterInToolbar: true,
            onCheckboxChange: (value: boolean, row: CompetitorMapping) => this.edit(row, { is_excluded: value })
        },
        {
            field: 'competitor_mapped',
            header: 'Competitor (Mapped)',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.competitorAutocomplete('competitor_mapped', q, context),
            editable: true,
            editType: FilterType.TEXT,
            onEditSave: (value, row) => {
                const mapped = typeof value === 'string' ? value.trimEnd() : String(value ?? '').trimEnd();
                this.edit(row, { competitor_mapped: mapped });
            }
        },
        {
            field: 'agency_type',
            header: 'Entity Type',
            filterType: FilterType.SELECT,
            filterOptionsFn: (context) => this.service.options(this.withDistrictContext(context)).pipe(
                map((o) => o.agency_types.map((v) => ({ label: v, value: v })))
            )
        },
        {
            field: 'tier',
            header: 'Tier',
            type: DataTableColumnType.NUMBER,
            filterType: FilterType.SELECT,
            filterOptionsFn: (context) => this.service.options(this.withDistrictContext(context)).pipe(
                map((o) => o.tiers)
            ),
            editable: true,
            editType: FilterType.SELECT,
            onEditSave: (value, row) => this.edit(row, { tier: value ?? null }),
        },
        {
            field: 'actions',
            header: '',
            type: DataTableColumnType.ACTIONS,
            sortable: false,
            filterable: false,
            actions: [
                {
                    icon: 'pi pi-eye',
                    label: 'View Rates',
                    command: (row: CompetitorMapping) =>
                        this.router.navigate(['/rates'], {
                            queryParams: {
                                agency_name_mapped: row.competitor_mapped?.trim() ? row.competitor_mapped : row.agency_name
                            }
                        }),
                    showIf: () => this.authService.hasPermission('rates.rates.read')
                }
            ]
        }
    ];
    districtMappingId: string | null | undefined;

    constructor(
        public authService: AuthService,
        private service: CompetitorMappingsService,
        private router: Router,
        private route: ActivatedRoute,
        private messageService: MessageService
    ) {
        this.districtMappingId = this.route.parent?.snapshot.paramMap.get('district_mapping_id');
    }

    ngOnInit(): void {
        this.service.options().subscribe((options) => {
            const tierColumn = this.columns.find((column) => column.field === 'tier');
            if (tierColumn) {
                tierColumn.editOptions = options.tiers;
            }
        });
    }

    loadData = (params: Record<string, any>) => this.service.listing(applyDistrictAndCompetitorToQuery(params, { districtMappingId: this.districtMappingId }));

    exportData = (params: Record<string, any>) => this.service.export(applyDistrictAndCompetitorToFilterObject(params, { districtMappingId: this.districtMappingId }));

    competitorAutocomplete(field: 'agency_name' | 'competitor_mapped', query: string, context: Record<string, any> = {}) {
        return this.service.autocomplete(field, query, this.withDistrictContext(context)).pipe(
            map((res) => res.data ?? res)
        );
    }

    private withDistrictContext(context: Record<string, any> = {}): Record<string, any> {
        return applyDistrictAndCompetitorToQuery(context, { districtMappingId: this.districtMappingId });
    }

    edit(row: CompetitorMapping, data: Record<string, any>): void {
        this.loading = true;
        this.service.edit(row.id, data).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'success',
                    detail: 'Competitor Mapping has been updated successfully.'
                });
                this.reloadTable$.next();
            },
            error: (err) => {
                this.loading = false;
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }
}
